import i18n from 'i18next';
import { Platform } from 'react-native';
import { importAfterMetroReady } from '@utils/metroDynamicImport';
import en from './languages/en.json';

type LocaleModule = { default: Record<string, unknown> };

/**
 * Web: lazy dynamic import (code-split).
 * Native: synchronous require — no lazy chunks / HMR issues.
 */
function makeLocaleLoader(
	requireLocale: () => Record<string, unknown>,
	importLocale: () => Promise<LocaleModule>
): () => Promise<LocaleModule> {
	if (Platform.OS !== 'web') {
		return async () => ({ default: requireLocale() });
	}
	return () => importAfterMetroReady(importLocale);
}

export type SupportedLocale =
  | 'en-GB'
  | 'ar'
  | 'de'
  | 'el'
  | 'es'
  | 'fil'
  | 'fr'
  | 'hi'
  | 'it'
  | 'ja'
  | 'ko'
  | 'ml'
  | 'ms'
  | 'pt'
  | 'si'
  | 'tk'
  | 'ur'
  | 'xh'
  | 'zh'
  | 'zu';

const LOCALE_LOADERS: Record<SupportedLocale, () => Promise<{ default: Record<string, unknown> }>> = {
  'en-GB': async () => ({ default: en }),
  ar: makeLocaleLoader(() => require('./languages/ar.json'), () => import('./languages/ar.json')),
  de: makeLocaleLoader(() => require('./languages/de.json'), () => import('./languages/de.json')),
  el: makeLocaleLoader(() => require('./languages/el.json'), () => import('./languages/el.json')),
  es: makeLocaleLoader(() => require('./languages/es.json'), () => import('./languages/es.json')),
  fil: makeLocaleLoader(() => require('./languages/fil.json'), () => import('./languages/fil.json')),
  fr: makeLocaleLoader(() => require('./languages/fr.json'), () => import('./languages/fr.json')),
  hi: makeLocaleLoader(() => require('./languages/hi.json'), () => import('./languages/hi.json')),
  it: makeLocaleLoader(() => require('./languages/it.json'), () => import('./languages/it.json')),
  ja: makeLocaleLoader(() => require('./languages/ja.json'), () => import('./languages/ja.json')),
  ko: makeLocaleLoader(() => require('./languages/ko.json'), () => import('./languages/ko.json')),
  ml: makeLocaleLoader(() => require('./languages/ml.json'), () => import('./languages/ml.json')),
  ms: makeLocaleLoader(() => require('./languages/ms.json'), () => import('./languages/ms.json')),
  pt: makeLocaleLoader(() => require('./languages/pt.json'), () => import('./languages/pt.json')),
  si: makeLocaleLoader(() => require('./languages/si.json'), () => import('./languages/si.json')),
  tk: makeLocaleLoader(() => require('./languages/tk.json'), () => import('./languages/tk.json')),
  ur: makeLocaleLoader(() => require('./languages/ur.json'), () => import('./languages/ur.json')),
  xh: makeLocaleLoader(() => require('./languages/xh.json'), () => import('./languages/xh.json')),
  zh: makeLocaleLoader(() => require('./languages/zh.json'), () => import('./languages/zh.json')),
  zu: makeLocaleLoader(() => require('./languages/zu.json'), () => import('./languages/zu.json')),
};

const loadedLocales = new Set<string>(['en', 'en-GB']);

export function normalizeLocale(lng?: string | null): SupportedLocale {
  const raw = String(lng || 'en-GB').trim();
  if (raw === 'en' || raw === 'English') return 'en-GB';
  if (raw in LOCALE_LOADERS) return raw as SupportedLocale;
  const base = raw.split('-')[0];
  if (base in LOCALE_LOADERS) return base as SupportedLocale;
  return 'en-GB';
}

export async function ensureLocaleLoaded(lng?: string | null): Promise<SupportedLocale> {
  const locale = normalizeLocale(lng);
  if (loadedLocales.has(locale)) {
    return locale;
  }

  const loader = LOCALE_LOADERS[locale];
  if (__DEV__) {
    console.log('[i18n] ensureLocaleLoaded: fetching', locale);
  }
  const LOAD_TIMEOUT_MS = 15000;
  let loadTimer: ReturnType<typeof setTimeout> | undefined;
  const mod = await Promise.race([
    loader(),
    new Promise<never>((_, reject) => {
      loadTimer = setTimeout(
        () => reject(new Error(`[i18n] locale chunk timeout: ${locale}`)),
        LOAD_TIMEOUT_MS
      );
    }),
  ]).finally(() => {
    if (loadTimer) clearTimeout(loadTimer);
  });
  if (__DEV__) {
    console.log('[i18n] ensureLocaleLoaded: loaded', locale);
  }
  i18n.addResourceBundle(locale, 'translation', mod.default, true, true);
  if (locale === 'en-GB') {
    i18n.addResourceBundle('en', 'translation', mod.default, true, true);
  }
  loadedLocales.add(locale);
  return locale;
}

export function readPersistedLanguage(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('persist:root');
    if (!raw) return null;
    const root = JSON.parse(raw) as { app?: string };
    if (!root?.app) return null;
    const app = JSON.parse(root.app) as { language?: { value?: string } | string };
    if (typeof app?.language === 'string') return app.language;
    return app?.language?.value ?? null;
  } catch {
    return null;
  }
}

const EN_RESOURCES = {
  en: { translation: en },
  'en-GB': { translation: en },
};

export { EN_RESOURCES };
